import { InputHTMLAttributes, forwardRef, createContext, useContext } from 'react';
import { cn } from '../utils/cn';

export interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  error?: string;
}

interface RadioGroupContextValue {
  name: string;
  value?: string | number;
  onChange?: (value: string) => void;
  error?: string;
}

const RadioGroupContext = createContext<RadioGroupContextValue | null>(null);

/**
 * Radio - Accessible radio button component
 * 
 * Required CSS classes:
 * - .radio-wrapper - Wrapper container
 * - .radio - Base radio input styles
 * - .radio-label - Label styles
 * - .radio-error - Error state styles
 * - .radio-error-message - Error message styles
 * 
 * Accessibility:
 * - Keyboard: Tab (focus), Arrow keys (navigate within group), Space (select)
 * - Screen reader: Announces label, checked state, and error
 * - Focus: Visible focus indicator
 * 
 * @example
 * <RadioGroup name="size" value={selectedSize} onChange={setSelectedSize}>
 *   <Radio value="small" label="Small" />
 *   <Radio value="medium" label="Medium" />
 *   <Radio value="large" label="Large" />
 * </RadioGroup>
 */
export const Radio = forwardRef<HTMLInputElement, RadioProps>(
  ({ className, label, error, id, value, ...props }, ref) => {
    const context = useContext(RadioGroupContext);
    const radioId = id || `${context?.name || 'radio'}-${value}`;
    const isChecked = context ? context.value === value : props.checked;
    const groupError = context?.error || error;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (context?.onChange && value !== undefined) {
        context.onChange(String(value));
      }
      props.onChange?.(e);
    };

    return (
      <div className="radio-wrapper">
        <input
          ref={ref}
          type="radio"
          id={radioId}
          name={context?.name || props.name}
          className={cn('radio', groupError && 'radio-error', className)}
          aria-invalid={!!groupError}
          aria-describedby={groupError ? `${radioId}-error` : undefined}
          checked={isChecked}
          value={value}
          onChange={handleChange}
          {...props}
        />
        {label && (
          <label htmlFor={radioId} className="radio-label">
            {label}
          </label>
        )}
        {groupError && !context && (
          <p id={`${radioId}-error`} className="radio-error-message" role="alert">
            {groupError}
          </p>
        )}
      </div>
    );
  }
);

Radio.displayName = 'Radio';

export interface RadioGroupProps {
  name: string;
  value?: string | number;
  onChange?: (value: string) => void;
  error?: string;
  children: React.ReactNode;
  className?: string;
}

/**
 * RadioGroup - Container for radio buttons with shared state
 * 
 * Required CSS classes:
 * - .radio-group - Group container
 * 
 * @example
 * <RadioGroup name="size" value={selectedSize} onChange={setSelectedSize}>
 *   <Radio value="small" label="Small" />
 *   <Radio value="medium" label="Medium" />
 * </RadioGroup>
 */
export function RadioGroup({
  name,
  value,
  onChange,
  error,
  children,
  className,
}: RadioGroupProps) {
  return (
    <RadioGroupContext.Provider value={{ name, value, onChange, error }}>
      <div className={cn('radio-group', className)} role="radiogroup" aria-invalid={!!error}>
        {children}
        {error && (
          <p className="radio-group-error-message" role="alert">
            {error}
          </p>
        )}
      </div>
    </RadioGroupContext.Provider>
  );
}
